import { memo } from "react";
import { useTranslation } from "@coworker/locales";
import { MediaGroupedListView } from "@coworker/apprestructured/src/shared/components/MediaGroupedListView/MediaGroupedListView";
import CompletableChecklistItem from "@coworker/apprestructured/src/task-management/components/CompletableChecklist/CompletableChecklistItem/CompletableChecklistItem";
import { MediasAndArticleCountResponse } from "@coworker/app/src/components/RoomSettings/types/media";
import {
  MediaOrderedDetails,
  ItemNoAndType,
} from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { ItemCardWithCopyNo } from "@coworker/apprestructured/src/shared/components/item-cards/ItemCardWithCopyNo/ItemCardWithCopyNo";
import { ItemCardLocalCombination } from "@coworker/apprestructured/src/shared/components/item-cards/ItemCardLocalCombination/ItemCardLocalCombination";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import { useCombinationArticleData } from "@coworker/app/src/components/RoomSettings/hooks/useCombinationArticle";
import { Article } from "@coworker/app/src/components/RoomSettings/types/article";
import styles from "./MediasCompletableOrderedDetailsModalContent.module.css";

interface MediasCompletableOrderedDetailsModalContentProps {
  itemNoAndType: ItemNoAndType;
  groupedMedias: Record<string, MediasAndArticleCountResponse[]>;
  mediasOrderedDetails: MediaOrderedDetails[];
  onTogglePiecesCompleted: (mediaId: string, articleId: string) => void;
  onTogglePtagsCompleted: (mediaId: string, articleId: string) => void;
  showQuantityLabel?: boolean;
}

export const MediasCompletableOrderedDetailsModalContent = memo(
  ({
    itemNoAndType,
    groupedMedias,
    mediasOrderedDetails,
    onTogglePiecesCompleted,
    onTogglePtagsCompleted,
    showQuantityLabel = false,
  }: MediasCompletableOrderedDetailsModalContentProps) => {
    const { t } = useTranslation();
    const { data: itemInfo } = useItemInfo(itemNoAndType.no);

    const isLocalCombination = itemNoAndType.type === "SCI";
    const firstMediaArticleId = mediasOrderedDetails[0]?.article_id;

    const { data: combinationArticle } = useCombinationArticleData(
      firstMediaArticleId,
      isLocalCombination
    );

    const { data: combinationItemInfo } = useItemInfo(
      (combinationArticle as Article)?.productArticleId ?? ""
    );

    return (
      <>
        {isLocalCombination && combinationItemInfo && (
          <ItemCardLocalCombination item={combinationItemInfo} />
        )}
        {!isLocalCombination && itemInfo && (
          <ItemCardWithCopyNo item={itemInfo} />
        )}
        <MediaGroupedListView
          groupedMediasByType={groupedMedias}
          mediasOrderedDetails={mediasOrderedDetails}
          showQuantityLabel={showQuantityLabel}
          renderControls={(media) => {
            const currentMedia = mediasOrderedDetails.find(
              (m) => m.id === media.mediaId && m.article_id === media.articleId
            );
            const piecesQuantity = currentMedia?.pieces_ordered?.quantity ?? 0;
            const piecesCompleted =
              currentMedia?.pieces_ordered?.completed ?? false;
            const ptagsQuantity = currentMedia?.ptags_ordered?.quantity ?? 0;
            const ptagsCompleted =
              currentMedia?.ptags_ordered?.completed ?? false;

            return (
              <div
                className={`${styles["checkboxContainer"]} ${styles["checkboxWrapper"]}`}
              >
                <CompletableChecklistItem
                  title={t("getPiecesString", { count: piecesQuantity })}
                  completed={piecesCompleted}
                  onCompletedChange={() =>
                    onTogglePiecesCompleted(media.mediaId, media.articleId)
                  }
                  disabled={false}
                />
                {ptagsQuantity > 0 && (
                  <CompletableChecklistItem
                    title={t("pTagsString", { count: ptagsQuantity })}
                    completed={ptagsCompleted}
                    onCompletedChange={() =>
                      onTogglePtagsCompleted(media.mediaId, media.articleId)
                    }
                    disabled={false}
                  />
                )}
              </div>
            );
          }}
        />
      </>
    );
  }
);
